"use client";

import React, { useEffect, useState } from "react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import { Nav, QuarklePlanet } from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import {
  WaveRule,
  Ribbon,
  TapeSticker,
  CornerPin,
  OutlineDisplay,
  BaselineGrid,
  Asterism,
  Sparkle,
} from "@/src/components/Home/LandingV6/ornaments";

const LAST_UPDATED = "August 2, 2026";
const EFFECTIVE = "August 1, 2026";

const TOC = [
  { id: "at-a-glance", label: "at a glance" },
  { id: "who", label: "who we are" },
  { id: "what", label: "what we collect" },
  { id: "how-used", label: "how we use it" },
  { id: "ai", label: "your writing & AI" },
  { id: "sharing", label: "who we share with" },
  { id: "rights", label: "your rights" },
  { id: "retention", label: "retention & deletion" },
  { id: "security", label: "security" },
  { id: "international", label: "international transfers" },
  { id: "cookies", label: "cookies & tracking" },
  { id: "children", label: "children" },
  { id: "changes", label: "changes to this policy" },
  { id: "contact", label: "contact" },
];

export default function PrivacyPolicyPage() {
  const [theme, setTheme] = useState("paper");

  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      {/* Hero */}
      <section style={{ padding: "70px 40px 40px", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: 40, right: -40, opacity: 0.1, pointerEvents: "none" }}>
          <OutlineDisplay text="privacy." size={280} color="var(--pink)" strokeW={1.8} />
        </div>
        <div aria-hidden style={{ position: "absolute", bottom: 20, left: 60 }}>
          <Asterism size={22} color="var(--clay)" />
        </div>
        <div aria-hidden style={{ position: "absolute", top: 90, right: 120 }}>
          <Sparkle size={30} color="var(--pink)" style={{ animation: "qv6-sparkle 3s infinite" }} />
        </div>
        <div style={{ maxWidth: 960, margin: "0 auto", position: "relative", textAlign: "center" }}>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
            <WaveRule w={60} color="var(--ink)" />
            <span className="caps" style={{ color: "var(--pink)" }}>privacy policy · plain english</span>
            <WaveRule w={60} color="var(--ink)" />
          </div>
          <h1
            className="tanker"
            style={{
              fontSize: "clamp(56px, 8vw, 140px)",
              lineHeight: 0.88,
              letterSpacing: "-0.04em",
              margin: "0 0 18px",
            }}
          >
            your <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>privacy</span>, in words.
          </h1>
          <p className="display" style={{ fontSize: 22, fontStyle: "italic", opacity: 0.85, maxWidth: 720, margin: "0 auto 22px", lineHeight: 1.5 }}>
            we treat your writing like mail: sealed, delivered, not snooped on. this is the long version, but we put the short one at the top.
          </p>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <CornerPin>last updated · {LAST_UPDATED}</CornerPin>
            <TapeSticker color="var(--lime)" rotate={-2}>reading time · about 9 minutes</TapeSticker>
          </div>
        </div>
      </section>

      {/* At a glance */}
      <AtAGlance />

      {/* TOC */}
      <TableOfContents />

      {/* Sections */}
      <LegalBody>
        <Section id="who" n="01" title="who we are" accent="var(--pink)">
          <P>
            <strong>Quarkle</strong> is operated by <strong>Quarkle Incorporated</strong>, a Delaware corporation with its principal place of business in Massachusetts, United States ("<strong>Quarkle</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"). This Privacy Policy explains how we collect, use, disclose, and protect information when you use our websites, applications, and services (collectively, the "<strong>Service</strong>").
          </P>
          <P>
            By using the Service, you agree to the practices described here. If you don't agree, please don't use the Service. This Policy is part of our <A href="/terms">Terms of Service</A>.
          </P>
        </Section>

        <Section id="what" n="02" title="what we collect" accent="var(--clay)">
          <SubH>Information you give us</SubH>
          <ul className="qv6-list">
            <li><strong>Account info:</strong> your name, email address, profile picture (via Auth0), and any other details you add to your account.</li>
            <li><strong>Your writing:</strong> the books, chapters, characters, notes, and other content you create, upload, or store in the Service. We'll call this your "<strong>Content</strong>." It's yours.</li>
            <li><strong>Payment info:</strong> processed by Stripe. We receive transaction metadata (plan, amount, last 4 digits) but never your full card number.</li>
            <li><strong>Communications:</strong> messages you send us (including via <A href="/feedback">feedback</A>, support, or email).</li>
          </ul>

          <SubH>Information we collect automatically</SubH>
          <ul className="qv6-list">
            <li><strong>Usage data:</strong> pages visited, features used, time spent, clicks, and performance metrics.</li>
            <li><strong>Device & technical data:</strong> IP address, browser type, operating system, device identifiers, referrer URL, and timestamps.</li>
            <li><strong>Cookies & similar tech:</strong> see <A href="#cookies">cookies & tracking</A> below.</li>
          </ul>

          <SubH>Information from third parties</SubH>
          <P>
            If you sign in with a provider like Google, we receive your name, email, and profile image from them. We may receive fraud-prevention or security signals from Stripe or Auth0 when relevant.
          </P>
        </Section>

        <Section id="how-used" n="03" title="how we use it" accent="var(--moss-2)">
          <P>We use the information above to:</P>
          <ul className="qv6-list">
            <li>run, maintain, and improve the Service;</li>
            <li>authenticate you, manage your subscription, and process payments;</li>
            <li>power the AI features you ask for: edits, critique, brainstorming, summaries, and the rest of the writing room;</li>
            <li>communicate with you about your account, updates, and (if you've opted in) occasional product news;</li>
            <li>diagnose bugs, prevent abuse, and keep the lights on;</li>
            <li>measure aggregate, de-identified usage to understand what to build next;</li>
            <li>comply with legal obligations, enforce our <A href="/terms">Terms</A>, and protect our rights and yours.</li>
          </ul>
          <Callout>
            <strong>Legal bases (for EEA/UK users).</strong> We rely on <em>contract</em> (to provide the Service), <em>legitimate interests</em> (to secure and improve it), <em>consent</em> (for optional marketing and non-essential cookies), and <em>legal obligation</em> (for tax, fraud, and compliance), whichever fits each use.
          </Callout>
        </Section>

        <Section id="ai" n="04" title="your writing & AI" accent="var(--pink)">
          <P>
            This is the question you probably came here to answer, so we'll be direct.
          </P>
          <ul className="qv6-list">
            <li><strong>Your Content is yours.</strong> You keep full ownership of anything you write, upload, or generate with Quarkle.</li>
            <li><strong>We do not use your Content to train our own AI models</strong>, and we don't sell or license your Content to anyone for model training. Not your drafts, not your characters, not your notes.</li>
            <li><strong>AI providers.</strong> Quarkle uses third-party AI and infrastructure providers to process the features you request. Depending on the model you select (or Quarkle selects in Auto mode), those providers may include Anthropic, OpenAI, Google, xAI, Moonshot AI/Kimi, and Amazon Web Services. The available roster can change as models are added or retired. We require that Content sent through our provider accounts is not used to train provider models under the applicable API, enterprise, or infrastructure terms. Providers may retain request and response data briefly for abuse monitoring or security in line with their applicable policies.</li>
            <li><strong>Aggregate, de-identified analytics.</strong> We may analyze aggregated, anonymized patterns (e.g., "Pro users edit ~2× per chapter") to improve the Service. We do not reconstruct this into anything that identifies you or reads your manuscript.</li>
            <li><strong>Opt-out isn't necessary.</strong> Because we don't train on your Content by default, there is nothing to opt out of. If that ever changes, we will ask you first.</li>
          </ul>
          <Callout accent="var(--pink)">
            Your book isn't someone else's training data. If that promise ever changes, it will be opt-in, explicit, and announced, not buried in a policy update.
          </Callout>
        </Section>

        <Section id="sharing" n="05" title="who we share with" accent="var(--sky)">
          <P>We share your information only with:</P>
          <ul className="qv6-list">
            <li><strong>Service providers</strong> that help us run the Service under written contracts requiring appropriate confidentiality and security, including:
              <ul className="qv6-list-nested">
                <li><strong>Cloud & storage:</strong> Amazon Web Services (hosting, databases, file storage).</li>
                <li><strong>Authentication:</strong> Auth0 (sign-in, identity).</li>
                <li><strong>Payments:</strong> Stripe (subscriptions, invoicing).</li>
                <li><strong>AI processing:</strong> model and infrastructure providers supporting the selected or automatically routed model, which may include Anthropic, OpenAI, Google, xAI, Moonshot AI/Kimi, and Amazon Web Services.</li>
                <li><strong>Analytics & error reporting:</strong> PostHog, Mixpanel, Microsoft Clarity, Google Analytics/Tag Manager, and Sentry. In the EEA, UK, and Switzerland, optional analytics load only after you consent; elsewhere they run by default and you can turn them off at any time.</li>
                <li><strong>Referral measurement:</strong> Upfluence, used to attribute referrals and purchases, subject to the same regional consent rules as analytics.</li>
                <li><strong>Web fonts:</strong> Google Fonts and Fontshare, which may receive basic request data such as IP address and browser information when font files load.</li>
                <li><strong>Email & communications:</strong> transactional-email providers for account notifications.</li>
              </ul>
            </li>
            <li><strong>Professional advisors</strong> (lawyers, accountants, auditors) under confidentiality.</li>
            <li><strong>Business transfers.</strong> If we're involved in a merger, acquisition, reorganization, or sale of assets, your information may transfer as part of that transaction. We'll give notice before your information becomes subject to a different privacy policy.</li>
            <li><strong>Legal and safety.</strong> We may disclose information to comply with law, respond to lawful requests from authorities, enforce our <A href="/terms">Terms</A>, protect the rights, property, or safety of Quarkle, our users, or the public, and investigate fraud or abuse.</li>
            <li><strong>With your consent</strong>, for anything else.</li>
          </ul>
          <P className="muted">We do not sell your personal information, and we do not "share" it for cross-context behavioral advertising as those terms are defined under the California Consumer Privacy Act (CCPA/CPRA).</P>
        </Section>

        <Section id="rights" n="06" title="your rights" accent="var(--lime)">
          <P>Depending on where you live, you have some or all of the following rights:</P>
          <ul className="qv6-list">
            <li><strong>Access:</strong> ask what information we hold about you.</li>
            <li><strong>Correction:</strong> fix information that's wrong or incomplete.</li>
            <li><strong>Deletion:</strong> ask us to delete your account and associated data.</li>
            <li><strong>Portability:</strong> receive a copy of your information in a common, machine-readable format; export your manuscripts anytime from the editor.</li>
            <li><strong>Object / restrict:</strong> object to processing based on legitimate interests, or ask us to restrict it.</li>
            <li><strong>Withdraw consent</strong> for anything you previously consented to (e.g., marketing emails), without affecting the lawfulness of prior processing.</li>
            <li><strong>Non-discrimination:</strong> we will not deny you service, charge you different prices, or give you worse service for exercising these rights.</li>
            <li><strong>Appeal (if we say no):</strong> you may appeal our decision, and in the EEA/UK you may also complain to your local data protection authority. California users may contact the California Attorney General.</li>
          </ul>
          <P>
            You can exercise most of these rights from inside your account. For anything you can't do from settings, email <Email>team@quarkle.ai</Email> and we will respond within the time required by applicable law (typically 30–45 days).
          </P>
          <P className="muted">
            We may need to verify your identity before acting on your request, and we may retain certain information where law allows: for example, to comply with tax, audit, or anti-fraud requirements, or to defend legal claims.
          </P>
        </Section>

        <Section id="retention" n="07" title="retention & deletion" accent="var(--clay)">
          <P>
            We retain personal information for as long as your account is active or as needed to provide the Service, comply with our legal obligations, resolve disputes, and enforce our agreements.
          </P>
          <ul className="qv6-list">
            <li>Your manuscripts and Content are retained while your account is active and for a reasonable grace period after cancellation so you can reactivate or export.</li>
            <li>Account identifiers and billing records are retained for the periods required by tax and accounting law (typically up to 7 years).</li>
            <li>Usage logs are retained for up to 24 months for security and analytics, unless we're required to keep them longer.</li>
            <li>When you delete your account, we remove or de-identify your Content and personal information on our active systems, with limited backups expiring on a rolling basis.</li>
          </ul>
        </Section>

        <Section id="security" n="08" title="security" accent="var(--moss-2)">
          <P>
            We use reasonable technical and organizational measures to protect your information: encryption in transit (TLS), encryption at rest for sensitive stores, access controls, least-privilege engineering, audit logging, and vetted sub-processors.
          </P>
          <P className="muted">
            No service can promise absolute security. If we become aware of a breach affecting your information, we'll notify you and the relevant authorities as required by law. Please use a strong, unique password and be mindful of phishing.
          </P>
        </Section>

        <Section id="international" n="09" title="international transfers" accent="var(--sky)">
          <P>
            Quarkle is based in the United States and our infrastructure (and several of our sub-processors) operate in the U.S. and other countries. If you access the Service from outside the U.S., your information will be transferred to, stored in, and processed in the U.S. and other jurisdictions whose data-protection laws may differ from yours.
          </P>
          <P>
            Where required, we rely on appropriate safeguards (including the European Commission's Standard Contractual Clauses, the UK International Data Transfer Addendum, and equivalent mechanisms for other regions) to protect international transfers.
          </P>
        </Section>

        <Section id="cookies" n="10" title="cookies & tracking" accent="var(--pink)">
          <P>We use a small number of cookies and similar technologies:</P>
          <ul className="qv6-list">
            <li><strong>Strictly necessary</strong>: to sign you in, keep you signed in, and protect against fraud. These can't be turned off without breaking the Service.</li>
            <li><strong>Preferences</strong>: to remember your theme, last-open book, and editor settings.</li>
            <li><strong>Optional analytics</strong>: PostHog, Mixpanel, Microsoft Clarity, and Google Analytics/Tag Manager help us understand product and site usage.</li>
            <li><strong>Optional referral measurement</strong>: Upfluence and Google Ads help us attribute referrals and completed purchases.</li>
            <li><strong>Error reporting</strong>: Sentry receives technical context when the app crashes, so we can fix it.</li>
          </ul>
          <P className="muted">
            In the EEA, UK, and Switzerland, we ask before loading optional analytics. Elsewhere, optional analytics run by default. In every region, use the privacy-choices control on any page to turn optional analytics off or back on at any time, and we honor the Global Privacy Control browser signal as an opt-out. Most browsers also let you refuse or delete stored data; doing so may reset preferences or sign you out. We do not sell personal information or use it for cross-site behavioral advertising.
          </P>
        </Section>

        <Section id="children" n="11" title="children" accent="var(--clay)">
          <P>
            The Service is not directed to children under 16. We do not knowingly collect personal information from anyone under 16. If you believe a child has provided us personal information, please contact <Email>team@quarkle.ai</Email> and we will delete it.
          </P>
        </Section>

        <Section id="changes" n="12" title="changes to this policy" accent="var(--moss-2)">
          <P>
            We may update this Policy from time to time. If we make material changes, we'll notify you by email (to the address associated with your account) or with a prominent notice inside the Service before the change takes effect. The "last updated" date at the top tells you when it most recently changed. Continued use after the effective date means you accept the updated Policy.
          </P>
        </Section>

        <Section id="contact" n="13" title="contact" accent="var(--pink)">
          <P>
            Privacy questions, requests, or concerns:
          </P>
          <ContactCard />
        </Section>
      </LegalBody>

      <Footer />
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */

function AtAGlance() {
  const items = [
    { title: "your work is yours", body: "you keep full ownership of your manuscripts, characters, and notes. we just help you write them.", accent: "var(--pink)" },
    { title: "never train-fodder", body: "we do not use your writing to train AI models, and our AI providers don't either.", accent: "var(--lime)" },
    { title: "export or delete", body: "take your books with you. delete your account and we'll remove your content from our active systems.", accent: "var(--sky)" },
    { title: "tracking is optional", body: "in the EEA, UK & Switzerland, analytics wait for your consent. elsewhere they're on until you turn them off, on any page.", accent: "var(--clay)" },
  ];
  return (
    <section style={{ padding: "30px 40px 60px", position: "relative" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <span className="caps" style={{ color: "var(--pink)" }}>the short version</span>
          <h2 className="tanker" style={{ fontSize: "clamp(34px, 4.4vw, 56px)", margin: "8px 0 0", lineHeight: 1 }}>
            if you read nothing else, read <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>this</span>.
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
          {items.map((it, i) => (
            <div
              key={i}
              style={{
                background: "var(--paper-3)",
                border: "1.5px solid var(--ink)",
                borderRadius: 12,
                padding: "22px 22px 20px",
                boxShadow: "6px 6px 0 var(--ink)",
                position: "relative",
              }}
            >
              <div aria-hidden style={{ position: "absolute", top: 12, right: 14, width: 10, height: 10, borderRadius: 999, background: it.accent, boxShadow: "0 0 0 3px var(--paper-3), 0 0 0 4px var(--ink)" }} />
              <h3 className="tanker" style={{ fontSize: 22, margin: "0 0 8px", lineHeight: 1.05 }}>{it.title}</h3>
              <p className="display" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, fontStyle: "italic", opacity: 0.85 }}>{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TableOfContents() {
  return (
    <section style={{ padding: "0 40px 60px", position: "relative" }}>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          background: "var(--paper-2)",
          border: "1.5px solid var(--ink)",
          borderRadius: 12,
          padding: "22px 24px",
          boxShadow: "6px 6px 0 var(--ink)",
        }}
      >
        <div className="caps" style={{ opacity: 0.7, marginBottom: 12 }}>the long version · contents</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "6px 22px" }}>
          {TOC.slice(1).map((t, i) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                padding: "4px 0",
                borderBottom: "1px dashed var(--rule)",
                color: "var(--ink)",
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              <span className="mono" style={{ fontSize: 10, opacity: 0.55, letterSpacing: "0.12em" }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="display" style={{ fontStyle: "italic", fontSize: 16 }}>{t.label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function LegalBody({ children }) {
  return (
    <section style={{ padding: "0 40px 80px", position: "relative" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        {children}
      </div>
      <style jsx global>{`
        .qv6 .qv6-legal-section p { font-size: 17px; line-height: 1.7; margin: 12px 0; color: var(--ink); opacity: 0.92; }
        .qv6 .qv6-legal-section p.muted { opacity: 0.7; font-size: 15px; }
        .qv6 .qv6-legal-section strong { font-weight: 600; }
        .qv6 .qv6-legal-section a { color: var(--ink); border-bottom: 1.5px solid var(--pink); text-decoration: none; }
        .qv6 .qv6-legal-section a:hover { background: var(--pink); color: #fff; }
        .qv6 .qv6-list {
          list-style: none;
          padding: 0;
          margin: 14px 0;
          display: grid;
          gap: 10px;
        }
        .qv6 .qv6-list > li {
          padding-left: 22px;
          position: relative;
          font-size: 17px;
          line-height: 1.65;
          opacity: 0.92;
        }
        .qv6 .qv6-list > li::before {
          content: "›";
          position: absolute;
          left: 0;
          top: 0;
          color: var(--pink);
          font-family: 'Gambarino', serif;
          font-weight: 700;
          font-size: 22px;
          line-height: 1.2;
        }
        .qv6 .qv6-list-nested {
          list-style: none;
          padding: 8px 0 0 4px;
          margin: 0;
          display: grid;
          gap: 6px;
        }
        .qv6 .qv6-list-nested > li {
          padding-left: 18px;
          position: relative;
          font-size: 15px;
          line-height: 1.6;
          opacity: 0.85;
        }
        .qv6 .qv6-list-nested > li::before {
          content: "·";
          position: absolute;
          left: 4px;
          top: -3px;
          color: var(--ink);
          font-weight: 700;
          font-size: 22px;
        }
      `}</style>
    </section>
  );
}

function Section({ id, n, title, accent, children }) {
  return (
    <article
      id={id}
      className="qv6-legal-section"
      style={{
        padding: "38px 0 32px",
        borderTop: "1.5px solid var(--ink)",
        scrollMarginTop: 120,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 14, flexWrap: "wrap" }}>
        <span className="tanker" style={{ fontSize: 72, color: accent, lineHeight: 0.85, fontStyle: "italic" }}>
          {n}
        </span>
        <div>
          <h2 className="tanker" style={{ fontSize: "clamp(30px, 3.6vw, 48px)", margin: 0, lineHeight: 1, letterSpacing: "-0.02em" }}>
            {title}
          </h2>
          <WaveRule w={100} color={accent} />
        </div>
      </div>
      {children}
    </article>
  );
}

function SubH({ children }) {
  return (
    <h3 className="display" style={{ fontSize: 22, fontStyle: "italic", margin: "22px 0 6px", color: "var(--ink)" }}>
      {children}
    </h3>
  );
}

function P({ children, className }) {
  return <p className={className}>{children}</p>;
}

function A({ href, children }) {
  const external = href?.startsWith("http");
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>
      {children}
    </a>
  );
}

function Email({ children }) {
  return (
    <a href={`mailto:${children}`} className="mono" style={{ fontSize: 15 }}>
      {children}
    </a>
  );
}

function Callout({ children, accent = "var(--lime)" }) {
  return (
    <aside
      style={{
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderLeft: `6px solid ${accent}`,
        borderRadius: 10,
        padding: "16px 18px",
        margin: "18px 0",
        fontSize: 16,
        lineHeight: 1.55,
        fontFamily: "'Gambarino', serif",
        fontStyle: "italic",
      }}
    >
      {children}
    </aside>
  );
}

function ContactCard() {
  return (
    <div
      style={{
        background: "var(--ink)",
        color: "var(--paper)",
        border: "1.5px solid var(--ink)",
        borderRadius: 12,
        padding: "24px 26px",
        boxShadow: "8px 8px 0 var(--pink)",
        marginTop: 16,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div className="caps" style={{ color: "var(--pink)", marginBottom: 10 }}>the people answering</div>
        <div className="tanker" style={{ fontSize: 28, lineHeight: 1.1 }}>Quarkle Incorporated</div>
        <div className="display" style={{ fontSize: 16, fontStyle: "italic", opacity: 0.85, marginTop: 4 }}>Delaware, USA · operating from Massachusetts</div>
        <div className="mono" style={{ fontSize: 12, opacity: 0.75, marginTop: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          effective {EFFECTIVE}
        </div>
      </div>
      <a
        href="mailto:team@quarkle.ai"
        className="mono"
        style={{
          background: "var(--pink)",
          color: "#fff",
          padding: "12px 18px",
          borderRadius: 999,
          border: "1.5px solid var(--paper)",
          fontSize: 13,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          textDecoration: "none",
          boxShadow: "4px 4px 0 var(--paper-3)",
        }}
      >
        team@quarkle.ai →
      </a>
    </div>
  );
}
